/**
 * Lane SCENE-PLAINS, BUILDINGS-R1 step 1: the curved wuxia roof. The slope is concave (flat at the eave, steep at the
 * ridge) and the eave line sags in the middle and sweeps up at the corners, as in the scenery refs
 * (docs/review/art/BUILDINGS-R1.md §1). `roof()` (buildings.ts) hands its painting to `curvedRoof` while
 * `CURVE.on`; every kit roof (hip, gable, hip-gable) goes through it. Pure projection maths on the caller's `P`, so it
 * holds at any camera pitch or yaw.
 */
import { valueNoise } from '@nine/shared';
import { faceRec, line, M, snap, type Pix, type Proj, type Pt, type Ramp } from '../buildings';

/** Switch for the curved roof (one flag, so the old planar roof stays one edit away). */
export const CURVE = { on: true };

const FALL = 1.7;

/** Corner lift (px) of an eave rectangle `[x0,y0,x1,y1]` whose roof rises `h` px. */
export function eaveLift(r: readonly number[], h: number): number {
  if (!CURVE.on) return 0;
  const len = Math.max(r[2]! - r[0]!, r[3]! - r[1]!);
  return Math.max(0, Math.min(10, 1.8 * len, 0.55 * Math.abs(h)));
}

const sag = (s: number): number => Math.pow(Math.abs(2 * s - 1), 2.2);

/** A point on the eave line between corners (ax,ay) and (bx,by), `s` 0..1, with its lift. */
export function eavePt(P: Proj, ax: number, ay: number, bx: number, by: number, ze: number, k: number, s: number): Pt {
  return P(ax + (bx - ax) * s, ay + (by - ay) * s, ze + k * sag(s));
}

/** A line along the eave (a→b), following the curve. */
export function eaveLine(px: Pix, P: Proj, a: Pt, b: Pt, ze: number, k: number, fn: (f: number) => ReturnType<typeof snap>, w = 1): void {
  const n = 12;
  for (let i = 0; i < n; i++) {
    const p = eavePt(P, a[0], a[1], b[0], b[1], ze, k, i / n);
    const q = eavePt(P, a[0], a[1], b[0], b[1], ze, k, (i + 1) / n);
    line(px, p, q, () => fn((i + 0.5) / n), w);
  }
}

/** A hip line from an eave corner up to a top corner, following the slope curve. */
export function hipLine(px: Pix, P: Proj, from: readonly [number, number], to: readonly [number, number], ze: number, zt: number, k: number, col: ReturnType<typeof snap>): void {
  let prev: Pt | null = null;
  const n = 14;
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const p = P(from[0] + (to[0] - from[0]) * f, from[1] + (to[1] - from[1]) * f, ze + (zt - ze) * Math.pow(f, FALL) + k * (1 - f) * (1 - f));
    if (prev) line(px, prev, p, () => col, 1);
    prev = p;
  }
}

/**
 * A tiled roof from an eave rectangle up to a top rectangle, same contract and look as `roof()` but curved: concave
 * fall line, eave sagging between lifted corners.
 */
export function curvedRoof(
  px: Pix,
  P: Proj,
  [x0, y0, x1, y1]: readonly [number, number, number, number],
  ze: number,
  [tx0, ty0, tx1, ty1]: readonly [number, number, number, number],
  zt: number,
  tiles: Ramp,
  edge: Ramp,
  ridge: boolean,
): void {
  const k = eaveLift([x0, y0, x1, y1], zt - ze);
  type V = readonly [number, number];
  const faces: [V, V, V, V, number][] = [
    [[x0, y0], [x1, y0], [tx1, ty0], [tx0, ty0], 0.48],
    [[x0, y1], [x0, y0], [tx0, ty0], [tx0, ty1], 0.3],
    [[x1, y0], [x1, y1], [tx1, ty1], [tx1, ty0], 0.68],
    [[x0, y1], [x1, y1], [tx1, ty1], [tx0, ty1], 0.42],
  ];
  if (faceRec.on)
    for (const [a, b, c, d] of faces)
      faceRec.faces.push({ kind: 'roof', pts: [[a[0], a[1], ze], [b[0], b[1], ze], [c[0], c[1], zt], [d[0], d[1], zt]] });
  for (const [a, b, c, d, light] of faces) {
    const pt = (s: number, f: number): Pt => {
      const ex = a[0] + (b[0] - a[0]) * s;
      const ey = a[1] + (b[1] - a[1]) * s;
      const fx = d[0] + (c[0] - d[0]) * s;
      const fy = d[1] + (c[1] - d[1]) * s;
      return P(ex + (fx - ex) * f, ey + (fy - ey) * f, ze + (zt - ze) * Math.pow(f, FALL) + k * sag(s) * (1 - f) * (1 - f));
    };
    const S = Math.max(Math.hypot(...sub(pt(1, 0), pt(0, 0))), Math.hypot(...sub(pt(1, 1), pt(0, 1))), 1);
    let T = 0;
    for (let i = 0; i < 8; i++) T += Math.hypot(...sub(pt(0.5, (i + 1) / 8), pt(0.5, i / 8)));
    T = Math.max(T, 1);
    const ns = Math.ceil(S * 2.5);
    const nt = Math.ceil(T * 2.5);
    for (let i = 0; i <= ns; i++) {
      const s = i / ns;
      for (let j = 0; j <= nt; j++) {
        const f = j / nt;
        const [sx, sy] = pt(s, f);
        const x = Math.round(s * S);
        const y = Math.round(f * T);
        const t = f * T;
        let l = light;
        let col;
        if (t < 2.6) col = (x & 3) < 2 ? snap(tiles, l + 0.3, x, y, 0) : snap(tiles, l - 0.45, x, y, 0);
        else {
          const ph = x & 3;
          l += ph === 0 ? 0.24 : ph === 1 ? 0.1 : ph === 2 ? -0.06 : -0.34;
          const r = (t - 2.6) % 5;
          if (r < 1) l -= 0.45;
          else if (r > 4) l += 0.1;
          l += (valueNoise((s * S) / 5, t / 9) - 0.5) * 0.05;
          col = snap(tiles, l, x, y);
        }
        px.set(sx, sy, col);
      }
    }
  }
  // The fascia along the eave on the two near sides, following the curve; corners already sweep up.
  eaveLine(px, P, [x0, y1], [x1, y1], ze, k, (f) => (f > 0.5 ? edge[1]! : edge[2]!), 2);
  eaveLine(px, P, [x1, y1], [x1, y0], ze, k, (f) => (f < 0.5 ? edge[edge.length - 2]! : edge[1]!), 2);
  // A small upturned tip at the three near corners.
  for (const [cx, cy, dx] of [[x0, y1, -1], [x1, y1, 0], [x1, y0, 1]] as const) {
    const [ex, ey] = P(cx, cy, ze + k);
    for (let i = 0; i < 4; i++) {
      const q = i / 3;
      px.set(ex + dx * q * q * 3, ey - i, edge[i < 2 ? edge.length - 2 : edge.length - 1]!);
    }
  }
  if (!ridge) return;
  if (tx0 !== tx1 || ty0 !== ty1) {
    line(px, P(tx0, ty0, zt), P(tx1, ty1, zt), (_, kk) => tiles[[2, 0, 0, 0, 1][kk]!]!, 5);
    for (const [rx, ry] of [P(tx0, ty0, zt), P(tx1, ty1, zt)]) {
      px.rect(rx - 1, ry - 7, 3, 7, tiles[1]!);
      px.rect(rx - 1, ry - 8, 2, 1, tiles[3]!);
      px.set(rx + 1, ry - 9, tiles[2]!);
      px.set(rx + 2, ry - 10, M.gold[3]!);
      px.set(rx + 2, ry - 11, M.gold[4]!);
    }
  } else {
    const [rx, ry] = P(tx0, ty0, zt);
    px.rect(rx - 2, ry - 2, 5, 3, tiles[1]!);
  }
}

const sub = (a: Pt, b: Pt): [number, number] => [a[0] - b[0], a[1] - b[1]];
